import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api_createFoodCatering, api_getSubTypes } from '../../../api/catering/foodCatering'; // Adjusted API import for creating and fetching food caterings and sub types
import { toast } from 'react-hot-toast';
import { myToastWarning } from '../../../helpers/myToast';

type SubType = {
	_id: string;
	name: string;
	description: string;
};

const AddFoodCatering = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>('');
	const [description, setDescription] = useState<string>('');
	const [selectedSubTypes, setSelectedSubTypes] = useState<SubType[]>([]);
	const [subTypes, setSubTypes] = useState<SubType[]>([]); // State to hold fetched subtypes
	const [customSubTypes, setCustomSubTypes] = useState<SubType[]>([]); // State to hold custom subtypes
	const navigate = useNavigate();

	useEffect(() => {
		fetchSubTypes();
	}, []);

	const fetchSubTypes = async () => {
		try {
			const res = await api_getSubTypes();
			if (res.isSuccess) {
				const uniqueSubTypes = res.data.filter((subType: SubType, index: number, self: SubType[]) =>
					index === self.findIndex((t) => t.name === subType.name)
				);
				setSubTypes(uniqueSubTypes); // Update state with unique subtypes
			} else {
				toast.error('Failed to fetch sub types');
			}
		} catch (error) {
			console.error('Error fetching sub types:', error);
			toast.error('Failed to fetch sub types');
		}
	};

	const handleSubTypeClick = (subType: SubType) => {
		if (!selectedSubTypes.some((item) => item._id === subType._id)) {
			setSelectedSubTypes([...selectedSubTypes, subType]);
		}
	};

	const handleAddCustomSubType = () => {
		setCustomSubTypes([...customSubTypes, { _id: '', name: '', description: '' }]);
	};

	const handleCustomSubTypeChange = (index: number, field: string, value: string) => {
		const updatedCustomSubTypes = [...customSubTypes];
		updatedCustomSubTypes[index] = {
			...updatedCustomSubTypes[index],
			[field]: value,
		};
		setCustomSubTypes(updatedCustomSubTypes);
	};

	const addFoodCatering = async () => {
		if (!name) {
			myToastWarning('Please Enter Name');
			return;
		}
		try {
			setLoading(true);
			const payload = {
				name,
				description,
				subFoodCatering: [
					...selectedSubTypes.map((subType) => ({
						name: subType.name,
						description: subType.description,
					})),
					...customSubTypes.map((subType) => ({
						name: subType.name,
						description: subType.description,
					})),
				],
			};
			const res = await api_createFoodCatering(payload);
			const message = typeof res.message === 'string' ? res.message : 'Success';
			if (res.isSuccess) {
				toast.success(message || 'food catering created successfully');
				setTimeout(() => {
					navigate(-1);
				}, 100);
			} else {
				toast.error(message || 'Failed to create food catering');
			}
		} catch (error) {
			console.error('Error creating food catering:', error);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, padding: '40px' }}>
			<div className='mb-3'>
				<h3>Add food catering</h3>
			</div>

			<div className='mb-3'>
				<label htmlFor='name' className='form-label'>
					Name
				</label>
				<input
					id='name'
					value={name}
					onChange={(e) => setName(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter name'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='description' className='form-label'>
					Description
				</label>
				<input
					id='description'
					value={description}
					onChange={(e) => setDescription(e.target.value)}
					style={{ width: '400px' }}
					className='form-control'
					placeholder='Enter description'
				/>
			</div>

			<div className='mb-3'>
				<label htmlFor='subTypes' className='form-label'>
					Select Sub Types
				</label>
				<div style={{ display: 'flex', flexWrap: 'wrap', width: '400px' }}>
					{subTypes.map((item) => (
						<button
							key={item._id}
							onClick={() => handleSubTypeClick(item)}
							style={{ margin: '5px', padding: '10px', cursor: 'pointer' }}
							className={`btn ${selectedSubTypes.some((sub) => sub._id === item._id) ? 'btn-secondary' : 'btn-light'}`}
						>
							{item.name}
						</button>
					))}
				</div>
			</div>

			<div className='mb-3'>
				<button onClick={handleAddCustomSubType} className='btn btn-secondary'>
					Add Custom Sub Type
				</button>
			</div>

			{customSubTypes.map((subType, index) => (
				<div key={index} className='mb-3' style={{ width: '400px' }}>
					<input
						value={subType.name}
						onChange={(e) => handleCustomSubTypeChange(index, 'name', e.target.value)}
						className='form-control'
						placeholder='Enter custom sub type name'
					/>
					<input
						value={subType.description}
						onChange={(e) => handleCustomSubTypeChange(index, 'description', e.target.value)}
						className='form-control'
						placeholder='Enter custom sub type description'
						style={{ marginTop: '10px' }}
					/>
				</div>
			))}

			<div className='mb-3'>
				<label className='form-label'>Selected Sub Types:</label>
				<ul>
					{selectedSubTypes.map((subType) => (
						<li key={subType._id}>
							{subType.name} - {subType.description}
						</li>
					))}
					{customSubTypes.map((subType, index) => (
						<li key={`custom-${index}`}>
							{subType.name} - {subType.description}
						</li>
					))}
				</ul>
			</div>

			<button onClick={addFoodCatering} type='button' className='btn btn-lg btn-primary' disabled={loading}>
				{loading ? 'Loading..' : 'Submit'}
			</button>
		</div>
	);
};

export default AddFoodCatering;
